import { useFetch } from '@vueuse/core';
import { apiDomain } from './useConstants';

/**
 * Composable for login attempts API operations
 */
export function useLoginAttempts() {
  /**
   * Get detailed information about a specific user's login attempts
   */
  async function getUserDetails(email) {
    const { data } = await useFetch(`${apiDomain}/api/login-attempts/user/${encodeURIComponent(email)}`)
      .get()
      .json();

    return data.value;
  }

  /**
   * Get user-specific statistics
   */
  async function getUserStats(email, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const queryString = params.toString();
    const url = `${apiDomain}/api/login-attempts/user/${encodeURIComponent(email)}/stats${queryString ? `?${queryString}` : ''}`;

    const { data } = await useFetch(url)
      .get()
      .json();

    return data.value;
  }

  /**
   * Reset failed attempts counter for a specific user
   */
  async function resetUserAttempts(email) {
    const { data } = await useFetch(`${apiDomain}/api/login-attempts/user/${encodeURIComponent(email)}/reset`)
      .post()
      .json();

    return data.value;
  }

  /**
   * Delete all login attempt history for a specific user
   */
  async function deleteUserHistory(email) {
    const { data } = await useFetch(`${apiDomain}/api/login-attempts/user/${encodeURIComponent(email)}/history`)
      .delete()
      .json();

    return data.value;
  }

  /**
   * Block a user manually
   */
  async function blockUser(email, blockDurationMinutes = 20) {
    console.log('blockUser called with:', { email, blockDurationMinutes });

    try {
      const { data } = await useFetch(`${apiDomain}/api/login-attempts/block`)
        .post({ email, blockDurationMinutes })
        .json();

      console.log('blockUser response:', data.value);
      return data.value;
    } catch (error) {
      console.error('blockUser error:', error);
      throw error;
    }
  }

  /**
   * Unblock a user
   */
  async function unblockUser(email) {
    const { data } = await useFetch(`${apiDomain}/api/login-attempts/unblock`)
      .post({ email })
      .json();

    return data.value;
  }

  /**
   * Get all login attempts with filtering and pagination
   */
  async function getLoginAttempts({ limit = 15, offset = 0, sort = 'createdAt', direction = 'desc', search = '' } = {}) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      order: sort,
      direction: direction.toLowerCase(),
      search: search || ''
    });

    const { data } = await useFetch(`${apiDomain}/api/login-attempts/?${params}`)
      .get()
      .json();

    return data.value;
  }

  /**
   * Perform cleanup operations
   */
  async function performCleanup(operation, olderThanDays = undefined) {
    const payload = { operation };
    if (olderThanDays !== undefined) {
      payload.olderThanDays = olderThanDays;
    }

    const { data } = await useFetch(`${apiDomain}/api/login-attempts/cleanup`)
      .post(payload)
      .json();

    return data.value;
  }

  return {
    // User-specific methods
    getUserDetails,
    getUserStats,
    resetUserAttempts,
    deleteUserHistory,

    // General methods
    blockUser,
    unblockUser,
    getLoginAttempts,
    performCleanup
  };
}
